import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'

import {
  PiArrowUUpLeft,
  PiArrowUUpRight,
  PiCaretDoubleLeft,
  PiCaretDoubleRight,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiCaretUp,
  PiChatText,
  PiClock,
  PiCopy,
  PiEnvelopeSimple,
  PiEye,
  PiFloppyDisk,
  PiPaperPlaneTilt,
  PiPlus,
  PiShuffle,
  PiStack,
  PiTrash,
  PiUser,
  PiWarning,
} from 'react-icons/pi'
import { toast, type ToastContentProps } from 'react-toastify'
import to from 'await-to-js'
import { useQueryClient } from '@tanstack/react-query'

import {
  buildContactTokens,
  fillVariables,
  detectUnfilledVariables,
  findCampaignColumnVariablesWithMissingValues,
  findUnknownTemplateVariables,
  getMissingValuesLabel,
  mergeVariables,
  parseFullName,
  type EmailVariable,
} from '../email-variables'
import { renderEmail } from '../../../lib/email-render/renderEmail'
import {
  containsSpintax,
  resolveSpintax,
  spintaxRng,
} from '../../../lib/email-render/resolveSpintax'
import { Spinner } from 'react-bootstrap'
import axios from 'axios'

import { BaseTextInput } from 'components/FormUtils/TextInput'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { StatusBadge } from 'components/FbUI/StatusBadge'
import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { Switch } from 'components/UI/Switch/Switch'
import { cn } from 'components/UI/cn'

import {
  Sequence,
  sequenceByCampaignKey,
  useCreateSequence,
  useGetCampaignSequence,
  useSetSequenceStepActive,
  useUpdateSequence,
  type CreateSequenceMutationRequest,
  type UpdateSequenceMutationRequest,
} from 'services/sequences'
import {
  type CampaignLeadReviewItem,
  useGetCampaignLeadReview,
} from '../campaigns_api'
import {
  SequenceForm,
  SequenceStepForm,
  defaultStep,
} from '../SequenceTab/SequenceStepFormItem'
import { specToSequenceForm } from './specToWizardForm'
import type { SequenceSpec } from '../sequence_spec_api'
import {
  StepHistoryState,
  StepRestoreAction,
  canRedo,
  canUndo,
  canUndoSnapshot,
  dropStaleStepIds,
  getUndoRedoIntent,
  isEditableTarget,
  pushEntry,
  redoStep,
  undoStep,
} from './stepHistory'
import {
  applyInheritedSubjectOnThreadReplyOn,
  clearInheritedSubjectOnThreadReplyOff,
  hydrateThreadReply,
  inheritedReplySubject,
  isThreadReplyOn,
  resolveThreadReplyForEmit,
  shouldCascadeSubjectToStep,
  subjectForEmit,
} from './threadReply'
import {
  getSequenceProceedBlockReason,
  type ProceedStepStatus,
} from './proceedGate'
import {
  MAX_MESSAGES_PER_STEP,
  groupStepsByFamily,
  resolveVariantWriteFields,
  variantLabelForIndex,
} from '../sequenceVariants'
import { EmailBodyEditor } from '../SequenceTab/EmailBodyEditor'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useMountEffect } from 'hooks/useMountEffect'
import { useDebouncedFlag } from 'hooks/useDebouncedFlag'
import { handleFormError } from 'utils/handleFormError'
import {
  useGetCampaignSettings,
  useUpdateCampaignSettings,
} from 'services/campaigns'
import { useGetEmailOverrides } from '../email_overrides_api'
import { useGetCampaignSenderEmails } from '../sender_emails_api'
import { FormatBadge } from '../FormatBadge'
import { FormatToggle } from '../FormatToggle'
import {
  SequenceSaveStatus,
  type SequenceSaveStatusValue,
} from '../SequenceSaveStatus'

import { LiveCampaignBanner } from './LiveCampaignBanner'
import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'
import { useCampaignAITokensByContact } from '../columns/useCampaignAITokens'
import { useCampaignVariables } from '../columns/useCampaignVariables'
import { SendStepTestEmailModal } from '../SendStepTestEmailModal'
import { SignatureDisclaimer } from '../SignatureDisclaimer'
import { EmailPreviewBody } from '../EmailPreviewBody'

interface WizardSequenceStepProps {
  campaignId: number
  campaignName: string
  bisonCampaignId?: number
  onNext: () => void
  onBack: () => void
  stepNumber: number
  totalSteps: number
  readOnly?: boolean
  isLiveCampaign?: boolean
  isActiveCampaign?: boolean
  backLabel?: string
  /**
   * Undo/redo history, owned by the wizard page so it survives Build step
   * unmounts (switching to Settings/Review and back within one wizard visit).
   */
  history: StepHistoryState
  onHistoryChange: (next: StepHistoryState) => void
  /**
   * When the user chose "create from existing", the source campaign's fetched
   * sequence spec (FIR-7659). The form hydrates from it once, then autosave
   * provisions the new sequence. Only consulted while this campaign has no
   * server sequence of its own; null/undefined for the blank flow.
   */
  sourceSpec?: SequenceSpec | null
  /** Open the page-level Templates drawer / Save-as-template modal. Mounted by
   *  the wizard page (not here) so the overlay also covers the step rail. */
  onOpenTemplates?: () => void
  onOpenSaveTemplate?: () => void
}

interface PreviewContact {
  id: string
  bisonLeadId: number | null
  label: string
  subtitle: string
  tokens: Record<string, string>
}

// Alias keeps existing SaveStatus references without a mass-rename.
type SaveStatus = SequenceSaveStatusValue

const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
const UNDO_SHORTCUT_HINT = IS_MAC ? '⌘Z' : 'Ctrl+Z'
const REDO_SHORTCUT_HINT = IS_MAC ? '⇧⌘Z' : 'Ctrl+Y'

/** Stable id so rapid deletions replace the delete toast instead of stacking. */
const STEP_DELETED_TOAST_ID = 'step-deleted'

const UNDO_REDO_BUTTON_CLASS =
  'flex size-7 items-center justify-center rounded text-fg-secondary transition-colors hover:bg-surface-alt hover:text-fg disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-fg-secondary'

const emptyDefaultValues: SequenceForm = {
  title: '',
  steps: [{ ...defaultStep }],
}

const EMPTY_SEQUENCE_STEPS: SequenceForm['steps'] = []

const SAMPLE_PREVIEW_CONTACT: PreviewContact = {
  id: 'sample-preview-contact',
  bisonLeadId: null,
  label: 'Sample Contact',
  subtitle: 'Example Company · Decision Maker',
  tokens: {
    FIRST_NAME: 'Alex',
    LAST_NAME: 'Morgan',
    EMAIL: 'alex@examplecompany.com',
    COMPANY: 'Example Company',
    TITLE: 'Decision Maker',
  },
}

function getApiErrorMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) {
    return 'Unable to save sequence. Please try again.'
  }

  const detail = err.response?.data?.detail
  if (typeof detail === 'string' && detail.length > 0) {
    return detail
  }

  return 'Unable to save sequence. Please try again.'
}

function stripHtml(value: string): string {
  // Parse via DOMParser so HTML entities decode — `getSemanticHTML()` from
  // Quill serializes apostrophes as `&#39;` and ampersands as `&amp;`, which
  // a regex-only strip would leave intact and render as literal entity text in
  // the collapsed-step first-line preview.
  const doc = new DOMParser().parseFromString(value, 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

// One-line collapsed-preview text. Wraps stripHtml but first turns <br> and
// block-element boundaries into spaces, so multi-paragraph bodies read
// "Line 1 Line 2" instead of running together as "Line 1Line 2". Kept separate
// from stripHtml so the content-presence checks that use stripHtml are unchanged.
function stripHtmlForPreview(value: string): string {
  return stripHtml(
    value
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, ' ')
  )
}

function toPreviewContact(contact: CampaignLeadReviewItem): PreviewContact {
  const { firstName, lastName } = parseFullName(contact.full_name)
  // Tokens coalesce a blank name to "there" (FIR-7704) via buildContactTokens,
  // so the preview renders exactly what the server sends. The label keeps the
  // raw name (or "Contact") so the contact picker doesn't read "There There"
  // for a nameless contact.
  return {
    id: `contact-${contact.contact_id}`,
    bisonLeadId: contact.bison_lead_id,
    label: `${firstName || 'Contact'} ${lastName}`.trim(),
    subtitle: [contact.company_name, contact.title].filter(Boolean).join(' · '),
    tokens: buildContactTokens(contact),
  }
}

/**
 * Subject-line banners, extracted so each per-step instance can host its
 * own `useDebouncedFlag` calls (hooks can't run in the parent's `.map`
 * callback). Wizard-top + editor-inline banners follow the same pattern.
 */
function SubjectValidationBanners({
  unknownVariables,
  missingVariables,
}: {
  unknownVariables: ReadonlyArray<string>
  missingVariables: ReadonlyArray<EmailVariable>
}) {
  const showUnknown = useDebouncedFlag(unknownVariables.length > 0)
  const showMissing = useDebouncedFlag(missingVariables.length > 0)
  return (
    <>
      {showUnknown && unknownVariables.length > 0 && (
        <Alert
          color="danger"
          className="mt-2"
          description={`Subject uses variables that are not available for this campaign: ${unknownVariables.join(
            ', '
          )}`}
        />
      )}
      {showMissing && missingVariables.length > 0 && (
        <Alert
          color="warning"
          className="mt-2"
          description={`Subject uses campaign column variables with missing values: ${missingVariables
            .map(
              (variable) =>
                `${variable.label} (${getMissingValuesLabel(variable)})`
            )
            .join(', ')}`}
        />
      )}
    </>
  )
}

export function WizardSequenceStep({
  campaignId,
  campaignName,
  bisonCampaignId,
  onNext,
  onBack,
  stepNumber,
  totalSteps,
  readOnly = false,
  isLiveCampaign = false,
  isActiveCampaign = false,
  backLabel = 'Back',
  history,
  onHistoryChange,
  sourceSpec = null,
  onOpenTemplates,
  onOpenSaveTemplate,
}: WizardSequenceStepProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  // Most recent successful save, shown by the pill as "Saved · 5m ago".
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [expandedStepIndex, setExpandedStepIndex] = useState(0)
  const [previewStepIndex, setPreviewStepIndex] = useState(0)
  const [previewContactId, setPreviewContactId] = useState<string>('')
  // Re-roll seed for the display-only spintax preview (EmailBison does the real
  // per-recipient variation at send time).
  const [randomizeSeed, setRandomizeSeed] = useState(0)
  const [showContactDropdown, setShowContactDropdown] = useState(false)
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)
  // Below xl the preview is an overlay pinned to the right edge; it defaults
  // to a slim collapsed bar so the editor keeps full width when the viewport
  // is too narrow for side-by-side. Ignored at xl+ (always-open column).
  const [previewCollapsed, setPreviewCollapsed] = useState(true)
  // Undo/redo history lives on the wizard page (see props) so it survives
  // Build step unmounts within a wizard visit. `history` drives render state
  // (undo/redo button enablement); `historyRef` is written synchronously by
  // `updateHistory` so the mount-once keydown listener and delete-toast click
  // handlers never act on a stale stack.
  const historyRef = useRef(history)
  historyRef.current = history
  const showContactDropdownRef = useRef(showContactDropdown)
  showContactDropdownRef.current = showContactDropdown
  useMountEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showContactDropdownRef.current) {
        setShowContactDropdown(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  })
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hydratedSequenceIdRef = useRef<number | null>(null)
  const hydratedFromSpecRef = useRef<number | null>(null)
  // Set the first time we hydrate from a copied source so the autosave below
  // fires immediately (no debounce) — the campaign provisions as soon as the
  // wizard opens with ?from=, so senders/settings copy without waiting (FIR-7659).
  const justHydratedFromSpecRef = useRef(false)
  const lastSavedSnapshotRef = useRef<string | null>(null)
  const saveChainRef = useRef<Promise<boolean>>(Promise.resolve(true))
  const latestDraftValuesRef = useRef<SequenceForm>(emptyDefaultValues)

  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId)
  const { data: leadReviewData } = useGetCampaignLeadReview(campaignId)
  const safeBisonCampaignId = bisonCampaignId ?? 0
  const { data: senderEmails } = useGetCampaignSenderEmails(safeBisonCampaignId)
  const senderSignature = senderEmails?.results?.[0]?.email_signature ?? ''
  const mailboxAddress = senderEmails?.results?.[0]?.email ?? null
  const multipleMailboxesAttached = (senderEmails?.results?.length ?? 0) > 1
  const { data: campaignSettings } = useGetCampaignSettings(
    campaignId,
    safeBisonCampaignId > 0
  )
  const updateSettingsMutation = useUpdateCampaignSettings()
  // Optimistic override for the format toggle. Lets the user switch
  // plain-text/HTML before the Bison campaign exists; cleared once the
  // setting is persisted (see flush effect below). Default to `true` because
  // new campaigns default to plain text — picking the wrong default would
  // briefly flash a rich-text editor on a plain-text campaign.
  const [pendingPlainText, setPendingPlainText] = useState<boolean | null>(null)
  const isPlainText = pendingPlainText ?? campaignSettings?.plain_text ?? true
  const { data: overrides } = useGetEmailOverrides(
    safeBisonCampaignId,
    safeBisonCampaignId > 0
  )

  // Set of lead IDs that have any overrides — O(1) lookup per contact in dropdown
  const overrideLeadIds = useMemo(
    () => new Set(overrides?.map((o) => o.lead_id) ?? []),
    [overrides]
  )

  const createSequenceMutation = useCreateSequence()
  const updateSequenceMutation = useUpdateSequence()
  const queryClient = useQueryClient()
  const setStepActiveMutation = useSetSequenceStepActive()
  const [togglingStepId, setTogglingStepId] = useState<number | null>(null)

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setError,
    reset,
    getValues,
    setValue,
  } = useForm<SequenceForm>({
    defaultValues: emptyDefaultValues,
    mode: 'onChange',
  })
  // useWatch, not watch(): plain watch() misses nested editor updates, so body edits stay stale.
  const watchedTitle = useWatch({ control, name: 'title' }) ?? ''

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: 'steps',
    keyName: 'fieldKey',
  })

  const watchedStepsValue = useWatch({ control, name: 'steps' })
  const watchedSteps = watchedStepsValue ?? EMPTY_SEQUENCE_STEPS

  const { enableSequenceVariants } = useFeatureFlagService()

  // Hover copy for the per-step reply-in-thread toggle comes from the
  // server-backed tooltip fixture system — never hardcoded (see
  // server/preferences/README.md).
  const { preferences } = usePreferences()
  const threadReplyTip = preferences?.tooltips?.['sequence_step_thread_reply']

  // Authoring uses a flat field array with variants kept adjacent right after
  // their family original (NOT id-based grouping — new rows have no id yet).
  // Per-row meta: slot number counts originals only; a variant inherits its
  // family original's slot and carries a stable "Variant A/B/C" label.
  const stepRowMeta = useMemo(() => {
    let slotNumber = 0
    // Variants run B, C, ... (the original is "Variant A"), so start at 1.
    let variantSeq = 1
    let originalIndex = -1
    return watchedSteps.map((step, index) => {
      if (step?.is_variant) {
        const label = variantLabelForIndex(variantSeq)
        variantSeq += 1
        return {
          isVariant: true,
          slotNumber,
          originalIndex,
          variantLabel: label,
        }
      }
      slotNumber += 1
      variantSeq = 1
      originalIndex = index
      // The original becomes "Variant A" only once it has a split. The forward
      // map decides the original's meta before seeing its variants, so peek the
      // next row to detect an adjacent variant.
      const hasVariant = watchedSteps[index + 1]?.is_variant === true
      return {
        isVariant: false,
        slotNumber,
        originalIndex: index,
        variantLabel: hasVariant ? variantLabelForIndex(0) : '',
      }
    })
  }, [watchedSteps])

  // Flat indices of the original (non-variant) rows — the preview navigates
  // slots, not individual variants.
  const originalIndexes = useMemo(
    () =>
      watchedSteps
        .map((step, index) => (step?.is_variant ? -1 : index))
        .filter((index) => index >= 0),
    [watchedSteps]
  )

  const campaignVariables = useCampaignVariables(campaignId)
  const allTemplateVariables = useMemo(
    () => mergeVariables(campaignVariables),
    [campaignVariables]
  )
  const aiTokensByContact = useCampaignAITokensByContact(campaignId)

  // Validation and proceed-gating only consider rows the user can see and fix:
  // with the flag off, hidden variants are excluded so flag-off behaves exactly
  // as today even when EmailBison-authored variants ride along in the form.
  const validatedSteps = useMemo(
    () =>
      enableSequenceVariants
        ? watchedSteps
        : watchedSteps.filter((step) => !step?.is_variant),
    [enableSequenceVariants, watchedSteps]
  )

  const missingCampaignColumnVariablesInTemplate =
    findCampaignColumnVariablesWithMissingValues(
      campaignVariables,
      validatedSteps.flatMap((step) => [
        step.email_subject ?? '',
        step.email_body ?? '',
      ])
    )
  const unknownVariablesInTemplate = findUnknownTemplateVariables(
    allTemplateVariables,
    validatedSteps.flatMap((step) => [
      step.email_subject ?? '',
      step.email_body ?? '',
    ])
  )

  // Hide the top-of-wizard validation banners while the user is actively
  // typing in the editor so transient mid-token states (e.g. `{F}`) do not
  // pop them open. Submit gating still uses the live arrays so a fast
  // Next click cannot bypass validation.
  const showUnknownTemplateBanner = useDebouncedFlag(
    unknownVariablesInTemplate.length > 0
  )
  const showMissingTemplateBanner = useDebouncedFlag(
    missingCampaignColumnVariablesInTemplate.length > 0
  )

  const previewContacts = useMemo(() => {
    const includedContacts =
      leadReviewData?.results?.filter((contact) => contact.is_included) ?? []
    if (includedContacts.length > 0) {
      return includedContacts.slice(0, 50).map((contact) => {
        const base = toPreviewContact(contact)
        const aiTokens = aiTokensByContact[contact.contact_id] ?? {}
        return { ...base, tokens: { ...base.tokens, ...aiTokens } }
      })
    }
    if ((leadReviewData?.summary.total ?? 0) === 0) {
      return [SAMPLE_PREVIEW_CONTACT]
    }
    return []
  }, [leadReviewData, aiTokensByContact])

  const hasCampaignContacts = (leadReviewData?.summary.total ?? 0) > 0
  const hasIncludedPreviewContacts =
    (leadReviewData?.results?.some((contact) => contact.is_included) ??
      false) === true
  const isUsingSamplePreview = !hasCampaignContacts
  const showNoIncludedContactsState =
    hasCampaignContacts && !hasIncludedPreviewContacts

  const activePreviewContact = useMemo(() => {
    if (showNoIncludedContactsState) {
      return null
    }
    return (
      previewContacts.find((contact) => contact.id === previewContactId) ??
      previewContacts[0]
    )
  }, [previewContactId, previewContacts, showNoIncludedContactsState])

  // The preview navigates slots (originals), never individual variants. Snap a
  // stale/variant index back to the nearest original at or before it.
  // The preview navigates messages: with the flag on, every rendered row
  // (originals + their adjacent variants); with it off, originals only —
  // matching what the step list renders. Originals are the slots, so the
  // flag-off path stays byte-for-byte today's behavior.
  const previewableIndexes = useMemo(
    () =>
      enableSequenceVariants
        ? watchedSteps.map((_, index) => index)
        : originalIndexes,
    [enableSequenceVariants, originalIndexes, watchedSteps]
  )
  const safePreviewStepIndex = useMemo(() => {
    if (!previewableIndexes.length) return 0
    if (previewableIndexes.includes(previewStepIndex)) return previewStepIndex
    const before = [...previewableIndexes]
      .reverse()
      .find((index) => index <= previewStepIndex)
    return before ?? previewableIndexes[0]
  }, [previewableIndexes, previewStepIndex])
  const previewPosition = Math.max(
    0,
    previewableIndexes.indexOf(safePreviewStepIndex)
  )
  const previewMeta = stepRowMeta[safePreviewStepIndex]
  const previewSlotNumber = previewMeta?.slotNumber ?? previewPosition + 1
  // "Step N · Variant X" for any labeled row (a split family's original carries
  // "Variant A"); a plain "Step N" for an unsplit step (empty variantLabel).
  const previewLabel = previewMeta?.variantLabel
    ? `Step ${previewSlotNumber} · ${previewMeta.variantLabel}`
    : `Step ${previewSlotNumber}`
  const previewStep = watchedSteps[safePreviewStepIndex]
  // Gap before the previewed slot = the previous slot original's wait_in_days
  // (EmailBison: delay after a step). Shown only when previewSlotNumber > 1.
  const previewIncomingDays =
    watchedSteps[originalIndexes[previewSlotNumber - 2]]?.wait_in_days ?? 1

  // Find override for the current preview contact + step combination
  const activeOverride = useMemo(() => {
    if (
      !overrides?.length ||
      !activePreviewContact?.bisonLeadId ||
      !previewStep?.id
    ) {
      return null
    }
    return (
      overrides.find(
        (o) =>
          o.lead_id === activePreviewContact.bisonLeadId &&
          o.step_id === previewStep.id
      ) ?? null
    )
  }, [overrides, activePreviewContact?.bisonLeadId, previewStep?.id])

  // Subject is rendered as a React text node which auto-escapes; using the
  // escaping interpolateVariables path here would double-escape.
  const rawPreviewSubject = previewStep
    ? activeOverride?.custom_subject || previewStep.email_subject || ''
    : ''
  const previewMode = isPlainText ? 'plaintext' : 'html'

  const rawPreviewBodyHtml =
    activeOverride?.custom_body || previewStep?.email_body || ''
  // Resolve spintax for subject + body from a single RNG (display only) so the
  // two vary independently rather than in lockstep, and the with- and
  // without-signature body renders below share one assembled variant; re-rolls
  // on randomizeSeed. Mirrors WizardReviewEmailsStep's getRenderedEmail.
  const { resolvedPreviewSubject, resolvedPreviewBodyHtml } = useMemo(() => {
    const rng = spintaxRng(randomizeSeed)
    return {
      resolvedPreviewSubject: resolveSpintax(rawPreviewSubject, rng),
      resolvedPreviewBodyHtml: resolveSpintax(rawPreviewBodyHtml, rng),
    }
  }, [rawPreviewSubject, rawPreviewBodyHtml, randomizeSeed])

  const filledPreviewSubject = fillVariables(
    resolvedPreviewSubject,
    activePreviewContact?.tokens ?? {}
  )
  const previewHasSpintax =
    containsSpintax(rawPreviewBodyHtml) || containsSpintax(rawPreviewSubject)
  // Memoize the rendered preview body — renderEmail does a full HTML parse +
  // tree walk and runs on every keystroke into the editor.
  const filledPreviewBody = useMemo(
    () =>
      previewStep
        ? renderEmail({
            bodyHtml: resolvedPreviewBodyHtml,
            signatureHtml: senderSignature,
            variables: activePreviewContact?.tokens ?? {},
            mode: previewMode,
          })
        : '',
    [
      previewStep,
      resolvedPreviewBodyHtml,
      senderSignature,
      activePreviewContact?.tokens,
      previewMode,
    ]
  )
  // "No content yet" fallback gates on the rendered body alone — checking the
  // raw template would miss the case where every body token resolves to empty
  // (e.g. `<p>{FIRST_NAME}</p>` with FIRST_NAME=""), which leaves a blank pane.
  const renderedBodyAlone = useMemo(
    () =>
      previewStep
        ? renderEmail({
            bodyHtml: resolvedPreviewBodyHtml,
            signatureHtml: '',
            variables: activePreviewContact?.tokens ?? {},
            mode: previewMode,
          })
        : '',
    [
      previewStep,
      resolvedPreviewBodyHtml,
      activePreviewContact?.tokens,
      previewMode,
    ]
  )
  const hasPreviewBodyContent = renderedBodyAlone.trim().length > 0

  // When the campaign has no contacts, the test send falls back to a sample so it
  // still works (FIR-7437) — EmailBison's random-lead endpoint 400s with zero
  // contacts. Reuse the preview's already-rendered subject/body (rendered against
  // SAMPLE_PREVIEW_CONTACT) so the emailed sample matches what's on screen.
  const sampleRenderedFor = useMemo(
    () =>
      isUsingSamplePreview && previewStep
        ? {
            subject: filledPreviewSubject,
            body: filledPreviewBody,
            plainText: isPlainText,
            contactName: activePreviewContact?.label ?? 'Sample Contact',
          }
        : null,
    [
      isUsingSamplePreview,
      previewStep,
      filledPreviewSubject,
      filledPreviewBody,
      isPlainText,
      activePreviewContact?.label,
    ]
  )

  // Warning detection uses the non-escaping fill so missing `{VARNAME}` tokens
  // survive for `detectUnfilledVariables` to find — `renderEmail` collapses
  // them to empty strings.
  const bodyForWarnings = previewStep
    ? fillVariables(rawPreviewBodyHtml, activePreviewContact?.tokens ?? {})
    : ''

  const previewWarnings = useMemo(
    () =>
      Array.from(
        new Set([
          ...detectUnfilledVariables(filledPreviewSubject),
          ...detectUnfilledVariables(bodyForWarnings),
        ])
      ),
    [bodyForWarnings, filledPreviewSubject]
  )

  const currentDraftValues = useMemo<SequenceForm>(
    () => ({
      title: watchedTitle,
      steps: watchedSteps.map((step) => ({
        id: step.id,
        email_subject: step.email_subject ?? '',
        email_body: step.email_body ?? '',
        wait_in_days: step.wait_in_days ?? defaultStep.wait_in_days,
        is_variant: step.is_variant ?? false,
        variant_from_step_id: step.variant_from_step_id ?? null,
        thread_reply: step.thread_reply ?? false,
      })),
    }),
    [watchedSteps, watchedTitle]
  )

  const normalizeValues = useCallback(
    (values: SequenceForm) => {
      const normalizedTitle = values.title.trim() || campaignName.trim()
      // The write fields (unique slot orders, shared variant wait, and the one
      // correct link field per the verified EmailBison contract) are resolved by
      // the shared helper so the create and update paths cannot drift.
      const writeFields = resolveVariantWriteFields(values.steps)
      const steps = values.steps.map((step, index) => {
        // The first non-variant row starts the thread → always false; later steps
        // emit their authored value. Mirrors the backend step-1 guard.
        const threadReply = resolveThreadReplyForEmit(values.steps, index)
        return {
          id: step.id,
          // EmailBison prepends its own `Re: ` to reply-in-thread steps, so strip
          // the builder's cosmetic `Re: ` before saving — otherwise the stored
          // subject doubles to `Re: Re: ...`. Non-reply steps persist verbatim.
          email_subject: subjectForEmit(step.email_subject, threadReply),
          email_body: step.email_body,
          ...writeFields[index],
          // After the spread so the step-1 thread-starter guard always wins.
          // resolveVariantWriteFields only returns variant-link fields today, but
          // ordering thread_reply last keeps it shadow-proof if that ever changes.
          thread_reply: threadReply,
        }
      })
      return { title: normalizedTitle, steps }
    },
    [campaignName]
  )

  const serializeValues = useCallback(
    (values: SequenceForm) =>
      JSON.stringify({
        sequenceId: sequence?.id ?? null,
        ...normalizeValues(values),
      }),
    [normalizeValues, sequence?.id]
  )

  const canSaveValues = (values: SequenceForm) => {
    const steps = enableSequenceVariants
      ? values.steps
      : values.steps.filter((step) => !step.is_variant)
    return (
      steps.length > 0 &&
      steps.every(
        (step) =>
          step.email_subject?.trim().length > 0 &&
          stripHtml(step.email_body || '').length > 0
      )
    )
  }

  const currentDraftSnapshot = serializeValues(currentDraftValues)
  const currentDraftHasAnyContent =
    currentDraftValues.title.trim().length > 0 ||
    currentDraftValues.steps.some(
      (step) => step.email_subject || step.email_body
    )
  const currentDraftCanSave =
    canSaveValues(currentDraftValues) &&
    missingCampaignColumnVariablesInTemplate.length === 0 &&
    unknownVariablesInTemplate.length === 0

  useEffect(() => {
    latestDraftValuesRef.current = currentDraftValues
  }, [currentDraftValues])

  useEffect(() => {
    if (sequence && hydratedSequenceIdRef.current !== sequence.id) {
      // Always group families (enabled: true) so variants hydrate adjacent to
      // their original and round-trip on save even when the flag is off — the
      // backend deletes any step missing from the save payload, so dropping
      // variants here would silently delete EmailBison-authored variant data.
      // The flag only controls rendering (collapse), not what the form holds.
      const orderedSteps = groupStepsByFamily(sequence.steps, {
        enabled: true,
      }).flatMap((family) => [
        family.original,
        ...family.variants.map((variant) => variant.step),
      ])
      const hydratedValues = {
        title: sequence.title || campaignName,
        steps: orderedSteps.map((step) => ({
          id: step.id,
          email_subject: step.email_subject,
          email_body: step.email_body,
          wait_in_days: step.wait_in_days ?? 1,
          is_variant: step.is_variant ?? false,
          variant_from_step_id: step.variant_from_step_id ?? null,
          active: step.active ?? true,
          // Render the stored value verbatim — pre-feature steps hydrate as false
          // (their true current behavior). No implicit default-on on hydration;
          // default-on applies only to newly added steps via addStep().
          thread_reply: hydrateThreadReply(step.thread_reply),
        })),
      }
      // A fresh load (nothing saved yet this session) is the only time
      // snapping the open editor + preview back to step 1 is the right default.
      // For a brand-new campaign the sequence id also arrives here for the first
      // time, but a save already ran (it set this ref) — that hydration is just
      // reconciling server-assigned step ids into the draft, so it must NOT
      // collapse the variant the user just opened. Preserve their position then.
      const isFreshLoad = lastSavedSnapshotRef.current === null
      hydratedSequenceIdRef.current = sequence.id
      reset({
        title: hydratedValues.title,
        steps: hydratedValues.steps,
      })
      lastSavedSnapshotRef.current = serializeValues(hydratedValues)
      setSaveStatus('saved')
      // Seed from server updated_at so the timestamp reflects persisted state, not page-load time.
      setLastSavedAt(
        sequence.updated_at ? new Date(sequence.updated_at) : new Date()
      )
      if (isFreshLoad) {
        setExpandedStepIndex(0)
        setPreviewStepIndex(0)
      }
    }
  }, [campaignName, reset, sequence, serializeValues])

  // Hydrate once from a chosen source's spec (FIR-7659). Only while this
  // campaign has no server sequence of its own — the moment a save provisions
  // one, the server-sequence effect above owns hydration and the page also
  // disables the spec fetch. lastSavedSnapshotRef is left null on purpose so the
  // autosave effect sees the copied content as unsaved and persists it.
  useEffect(() => {
    if (sequence || readOnly || !sourceSpec) return
    const sourceKey = sourceSpec.sequence?.id ?? -1
    if (hydratedFromSpecRef.current === sourceKey) return
    hydratedFromSpecRef.current = sourceKey

    const hydrated = specToSequenceForm(sourceSpec, campaignName)
    reset({ title: hydrated.title, steps: hydrated.steps })
    justHydratedFromSpecRef.current = true
    setExpandedStepIndex(0)
    setPreviewStepIndex(0)
  }, [sequence, readOnly, sourceSpec, campaignName, reset])

  useEffect(() => {
    if (sequence || !campaignName.trim()) return
    if (getValues('title').trim()) return

    setValue('title', campaignName, {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [campaignName, getValues, sequence, setValue])

  useEffect(() => {
    if (showNoIncludedContactsState || !previewContacts.length) {
      return
    }
    const hasCurrentSelection = previewContacts.some(
      (contact) => contact.id === previewContactId
    )
    if (!previewContactId || !hasCurrentSelection) {
      setPreviewContactId(previewContacts[0].id)
    }
  }, [previewContactId, previewContacts, showNoIncludedContactsState])

  const enqueueSave = useCallback(
    (
      values: SequenceForm,
      options?: { silent?: boolean }
    ): Promise<boolean> => {
      if (readOnly) {
        return Promise.resolve(true)
      }

      const snapshot = serializeValues(values)
      if (lastSavedSnapshotRef.current === snapshot) {
        return Promise.resolve(true)
      }

      saveChainRef.current = saveChainRef.current
        .catch(() => true)
        .then(async () => {
          setSaveStatus('saving')

          if (!options?.silent) {
            setIsSaving(true)
          }

          try {
            // An undone deletion can restore a step whose Bison id a prior
            // autosave already deleted. Drop ids the server no longer knows
            // (read from the canonical cache *now*, after any queued save
            // ahead of us has updated it) so the step is recreated instead
            // of failing Bison's update-existing-steps PUT.
            const canonicalSteps =
              queryClient.getQueryData<Sequence | null>(
                sequenceByCampaignKey(campaignId)
              )?.steps ?? sequence?.steps
            const knownStepIds = new Set(
              (canonicalSteps ?? []).map((step) => step.id)
            )
            const normalized = normalizeValues({
              ...values,
              steps: dropStaleStepIds(values.steps, knownStepIds),
            })

            if (sequence) {
              const payload: UpdateSequenceMutationRequest = {
                title: normalized.title,
                steps: normalized.steps,
              }

              const [err] = await to(
                updateSequenceMutation.mutateAsync({
                  id: sequence.id,
                  campaign_id: campaignId,
                  data: payload,
                })
              )

              if (err) {
                handleFormError(err, setError)
                if (!options?.silent) {
                  toast.error(getApiErrorMessage(err))
                }
                setSaveStatus('error')
                return false
              }
            } else {
              const payload: CreateSequenceMutationRequest = {
                campaign_id: campaignId,
                title: normalized.title,
                steps: normalized.steps.map(
                  ({
                    order,
                    email_subject,
                    email_body,
                    wait_in_days,
                    is_variant,
                    variant_from_step,
                    variant_from_step_id,
                  }) => ({
                    order,
                    email_subject,
                    email_body,
                    wait_in_days,
                    is_variant,
                    variant_from_step,
                    variant_from_step_id,
                  })
                ),
              }

              const [err] = await to(
                createSequenceMutation.mutateAsync({ data: payload })
              )

              if (err) {
                handleFormError(err, setError)
                if (!options?.silent) {
                  toast.error(getApiErrorMessage(err))
                }
                setSaveStatus('error')
                return false
              }
            }

            lastSavedSnapshotRef.current = snapshot
            setSaveStatus('saved')
            setLastSavedAt(new Date())
            return true
          } finally {
            if (!options?.silent) {
              setIsSaving(false)
            }
          }
        })

      return saveChainRef.current
    },
    [
      campaignId,
      createSequenceMutation,
      normalizeValues,
      queryClient,
      readOnly,
      sequence,
      serializeValues,
      setError,
      updateSequenceMutation,
    ]
  )

  useEffect(() => {
    if (readOnly) {
      setSaveStatus('idle')
      return
    }

    if (!currentDraftHasAnyContent) {
      setSaveStatus(sequence ? 'saved' : 'idle')
      return
    }

    const lastSavedSnapshot = lastSavedSnapshotRef.current

    if (lastSavedSnapshot === currentDraftSnapshot) {
      setSaveStatus((current) => (current === 'saving' ? current : 'saved'))
      return
    }

    setSaveStatus('unsaved')

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
    }

    if (!currentDraftCanSave) {
      return
    }

    // Fire immediately right after a from-existing hydration so the campaign
    // provisions on open; otherwise debounce normal typing. Decide the delay at
    // fire time (not schedule time) so an intervening re-render can't clear the
    // flag before the timer reads it.
    const saveDelayMs = justHydratedFromSpecRef.current ? 0 : 1800

    autosaveTimerRef.current = setTimeout(() => {
      justHydratedFromSpecRef.current = false
      void enqueueSave(latestDraftValuesRef.current, { silent: true })
    }, saveDelayMs)

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current)
      }
    }
  }, [
    currentDraftCanSave,
    currentDraftHasAnyContent,
    currentDraftSnapshot,
    enqueueSave,
    readOnly,
    sequence,
  ])

  const handlePrimarySubjectChange = (subject: string) => {
    setValue('steps.0.email_subject', subject, {
      shouldDirty: true,
      shouldValidate: true,
    })

    const currentSteps = getValues('steps')
    currentSteps.forEach((step, index) => {
      // Skip step 1, variants (they own their subject), and any step with
      // reply-to-thread turned OFF — an off step starts a new thread with its
      // own real subject, so it must not be overwritten with "Re: ...".
      if (!shouldCascadeSubjectToStep(currentSteps, index)) return
      const existingSubject = step.email_subject?.trim() ?? ''
      if (existingSubject === '' || existingSubject.startsWith('Re: ')) {
        setValue(
          `steps.${index}.email_subject`,
          inheritedReplySubject(subject),
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        )
      }
    })
  }

  // Single writer: the handler only stages the optimistic value. The flush
  // effect below owns the actual PUT, which prevents the double-fire bug
  // that came from having two code paths racing to send the same setting.
  const handleFormatChange = useCallback((nextPlainText: boolean) => {
    setPendingPlainText(nextPlainText)
  }, [])

  // Flush the buffered toggle once the Bison campaign comes into existence
  // (`safeBisonCampaignId` flipping from 0 to positive). Held in a ref so
  // the mutation object's ever-changing reference doesn't retrigger this
  // effect every render. (That previously fired a Settings PUT on every
  // render while one was in flight, FIR-7351 follow-up.)
  const settingsMutationRef = useRef(updateSettingsMutation)
  settingsMutationRef.current = updateSettingsMutation
  useEffect(() => {
    if (pendingPlainText === null) return
    if (safeBisonCampaignId <= 0) return
    if (campaignSettings?.plain_text === pendingPlainText) {
      setPendingPlainText(null)
      return
    }
    void settingsMutationRef.current
      .mutateAsync({
        campaign_id: campaignId,
        data: { plain_text: pendingPlainText },
      })
      .then(() => setPendingPlainText(null))
      .catch(() => {
        setPendingPlainText(null)
        toast.error('Failed to update email format')
      })
  }, [
    pendingPlainText,
    safeBisonCampaignId,
    campaignSettings?.plain_text,
    campaignId,
  ])

  const focusInsertedStep = (index: number) => {
    setExpandedStepIndex(index)
    setPreviewStepIndex(index)
  }

  const adjustIndicesAfterRemove = (index: number) => {
    const shift = (current: number) => {
      if (current > index) return current - 1
      if (current === index) return Math.max(0, index - 1)
      return current
    }
    setExpandedStepIndex(shift)
    setPreviewStepIndex(shift)
  }

  /**
   * Single write path for history: keeps `historyRef` in sync with state.
   * Any mutation invalidates an open delete toast's Undo (its snapshot depth
   * no longer matches), so the toast is dismissed rather than left as a dead
   * button; a new deletion re-shows it immediately via `keepDeleteToast`.
   */
  const updateHistory = (
    next: StepHistoryState,
    { keepDeleteToast = false }: { keepDeleteToast?: boolean } = {}
  ) => {
    historyRef.current = next
    onHistoryChange(next)
    if (!keepDeleteToast && toast.isActive(STEP_DELETED_TOAST_ID)) {
      toast.dismiss(STEP_DELETED_TOAST_ID)
    }
  }

  /**
   * Record a structural operation. Returns the new undo-stack depth so the
   * delete toast can snapshot which operation its Undo button refers to.
   */
  const recordStepOperation = (
    kind: 'add' | 'remove',
    index: number,
    step: SequenceStepForm
  ): number => {
    const next = pushEntry(historyRef.current, { kind, index, step })
    // A remove is immediately followed by showStepDeletedToast for this very
    // operation, so the existing toast is updated in place, not dismissed.
    updateHistory(next, { keepDeleteToast: kind === 'remove' })
    return next.past.length
  }

  const applyRestoreAction = (action: StepRestoreAction) => {
    if (action.type === 'insert') {
      insert(action.index, { ...action.step })
      focusInsertedStep(action.index)
      return
    }
    remove(action.index)
    adjustIndicesAfterRemove(action.index)
  }

  const getStepAtIndex = (index: number): SequenceStepForm | undefined =>
    getValues(`steps.${index}`)

  const handleUndo = () => {
    const result = undoStep(historyRef.current, getStepAtIndex)
    if (!result) return
    applyRestoreAction(result.action)
    updateHistory(result.state)
  }

  const handleRedo = () => {
    const result = redoStep(historyRef.current, getStepAtIndex)
    if (!result) return
    applyRestoreAction(result.action)
    updateHistory(result.state)
  }

  // Refs so the mount-once keydown listener and delete toasts always invoke
  // the latest handlers (which close over current history/form state).
  const handleUndoRef = useRef(handleUndo)
  handleUndoRef.current = handleUndo
  const handleRedoRef = useRef(handleRedo)
  handleRedoRef.current = handleRedo
  const readOnlyRef = useRef(readOnly)
  readOnlyRef.current = readOnly

  /**
   * Toast with an Undo button scoped to one specific deletion. `undoDepth` is
   * the undo-stack depth right after that deletion was recorded; the click
   * handler only undoes while the stack is still at that depth, so a later
   * structural op (or keyboard undo) within the toast's lifetime can't be
   * reversed by mistake. The stable toastId makes rapid deletions replace the
   * existing toast instead of stacking several ambiguous ones.
   */
  const showStepDeletedToast = (stepLabel: string, undoDepth: number) => {
    const renderToast = ({ closeToast }: ToastContentProps) => (
      <div className="flex w-full items-center justify-between gap-3">
        <span>{stepLabel} deleted</span>
        <button
          type="button"
          className="shrink-0 text-sm font-medium text-brand-bright transition-colors hover:text-brand-active"
          onClick={() => {
            if (canUndoSnapshot(historyRef.current, undoDepth)) {
              handleUndoRef.current()
            }
            closeToast()
          }}
        >
          Undo
        </button>
      </div>
    )
    // react-toastify dedupes (not replaces) on toastId, so an active toast
    // must be updated in place to show the newest deletion and restart the
    // 5s timer.
    if (toast.isActive(STEP_DELETED_TOAST_ID)) {
      toast.update(STEP_DELETED_TOAST_ID, {
        render: renderToast,
        autoClose: 5000,
      })
    } else {
      toast.info(renderToast, {
        autoClose: 5000,
        toastId: STEP_DELETED_TOAST_ID,
      })
    }
  }

  const addStep = () => {
    const firstSubject = getValues('steps.0.email_subject') ?? ''
    const nextIndex = watchedSteps.length
    // Default-on for steps after the first (reply in the prior thread); step 1
    // always starts the thread.
    const threadReply = nextIndex !== 0
    const newStep: SequenceStepForm = {
      ...defaultStep,
      wait_in_days: nextIndex === 0 ? defaultStep.wait_in_days : 3,
      // Only seed the inherited "Re:" subject when the new step replies in the
      // thread. A step that starts a new thread keeps its own (blank) subject.
      email_subject: threadReply ? inheritedReplySubject(firstSubject) : '',
      thread_reply: threadReply,
    }
    append(newStep)
    recordStepOperation('add', nextIndex, newStep)
    focusInsertedStep(nextIndex)
  }

  // Index just past a family's last variant — the insertion point for both a
  // duplicated step and a new variant so adjacency (original then its variants)
  // is preserved.
  const familyEndIndex = (originalIndex: number) => {
    const steps = getValues('steps')
    let end = originalIndex + 1
    while (end < steps.length && steps[end]?.is_variant) end += 1
    return end
  }

  const duplicateStep = (index: number) => {
    const step = getValues(`steps.${index}`)
    const insertAt = familyEndIndex(index)
    const copy: SequenceStepForm = {
      email_subject: step.email_subject,
      email_body: step.email_body,
      wait_in_days: step.wait_in_days,
      is_variant: false,
      variant_from_step_id: null,
    }
    insert(insertAt, copy)
    recordStepOperation('add', insertAt, copy)
    focusInsertedStep(insertAt)
  }

  const addVariant = (originalIndex: number) => {
    const original = getValues(`steps.${originalIndex}`)
    const insertAt = familyEndIndex(originalIndex)
    // Seed from the latest message in the family (the row just before the insert
    // point): the original for the first split, the previous split thereafter.
    // Authors usually only tweak the subject line, so duplicating the freshest
    // body beats always copying the original. Timing and the family link still
    // come from the original — only the copied content source changes.
    const latest = getValues(`steps.${insertAt - 1}`) ?? original
    const variant: SequenceStepForm = {
      email_subject: latest?.email_subject ?? '',
      email_body: latest?.email_body ?? '',
      wait_in_days: original?.wait_in_days ?? defaultStep.wait_in_days,
      is_variant: true,
      variant_from_step_id: original?.id ?? null,
    }
    insert(insertAt, variant)
    recordStepOperation('add', insertAt, variant)
    setExpandedStepIndex(insertAt)
  }

  // Pause/resume a saved variant via its own endpoint (separate from the
  // sequence save). No optimistic update: reflect the confirmed server state.
  // Deliberately NOT in the undo history: it's an immediate server mutation on
  // a live campaign (not a draft edit), and the row's Pause/Resume control is
  // already its own one-click inverse — Cmd+Z silently re-firing a live API
  // call would be surprising.
  const handleToggleVariantActive = async (vIndex: number) => {
    const step = getValues(`steps.${vIndex}`)
    if (!step?.id) return
    const next = step.active === false
    setTogglingStepId(step.id)
    const [err] = await to(
      setStepActiveMutation.mutateAsync({
        bison_step_id: step.id,
        campaign_id: campaignId,
        data: { active: next },
      })
    )
    setTogglingStepId(null)
    if (err) {
      toast.error(getApiErrorMessage(err))
      return
    }
    setValue(`steps.${vIndex}.active`, next, { shouldDirty: false })
  }

  const removeStepAtIndex = (index: number) => {
    const steps = getValues('steps')
    // Never orphan variants: an original keeps exactly one family; block removing
    // it while any variant remains (the UI also hides the control — this is the
    // backstop). It also keeps every history entry a single-row operation, so
    // undo/redo never has to restore a family atomically.
    if (!steps[index]?.is_variant && steps[index + 1]?.is_variant) {
      return
    }
    const removedStep = steps[index]
    // Label from row meta, captured before removal: the flat index is no longer
    // the step number once variants exist, and a variant reads as
    // "Step N · Variant B" like every other surface.
    const meta = stepRowMeta[index]
    const removedLabel = meta?.isVariant
      ? `Step ${meta.slotNumber} · ${meta.variantLabel}`
      : `Step ${meta?.slotNumber ?? index + 1}`
    remove(index)
    const undoDepth = recordStepOperation('remove', index, removedStep)
    adjustIndicesAfterRemove(index)
    showStepDeletedToast(removedLabel, undoDepth)
  }

  useMountEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (readOnlyRef.current) return
      const intent = getUndoRedoIntent(e)
      if (!intent) return
      // Text inputs and the Quill rich-text editor own their native undo
      // stacks while focused; the app-level stack only handles structural
      // step operations triggered from outside those surfaces.
      if (isEditableTarget(e.target)) return
      e.preventDefault()
      if (intent === 'undo') {
        handleUndoRef.current()
      } else {
        handleRedoRef.current()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  })

  const onSubmit = handleSubmit(async (values) => {
    if (readOnly) {
      onNext()
      return
    }

    if (missingCampaignColumnVariablesInTemplate.length > 0) {
      toast.warning(
        'Some campaign column variables are missing values for campaign contacts. Fill those cells before continuing.'
      )
      return
    }
    if (unknownVariablesInTemplate.length > 0) {
      toast.warning(
        'This sequence uses variables that are not available for this campaign. Remove them before continuing.'
      )
      return
    }

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
    }
    const success = await enqueueSave(values)
    if (success) {
      onNext()
    }
  })

  const canProceed =
    !readOnly &&
    isValid &&
    missingCampaignColumnVariablesInTemplate.length === 0 &&
    unknownVariablesInTemplate.length === 0 &&
    validatedSteps.length > 0 &&
    validatedSteps.every(
      (step) =>
        step.email_subject?.trim().length > 0 &&
        stripHtml(step.email_body || '').length > 0
    )

  // When Continue is disabled, tell the user *what* is missing instead of
  // leaving a dead button (FIR-7705). Surfaced via the footer `subtext` slot —
  // the same affordance WizardSettingsStep uses to explain a gated step. Step
  // labels mirror the list (slot number + variant); content checks mirror
  // `canProceed` exactly so the hint can never disagree with the button.
  const proceedBlockReason = useMemo(() => {
    if (readOnly || canProceed) return null
    const statuses: ProceedStepStatus[] = []
    watchedSteps.forEach((step, index) => {
      // Skip hidden variants when the flag is off so the hint matches the rows
      // the user can actually see and fix (mirrors `validatedSteps`).
      if (!step || (!enableSequenceVariants && step.is_variant)) return
      const meta = stepRowMeta[index]
      const slotNumber = meta?.slotNumber ?? index + 1
      statuses.push({
        label: meta?.variantLabel
          ? `Step ${slotNumber} · ${meta.variantLabel}`
          : `Step ${slotNumber}`,
        // Gates on the cosmetic form subject (may carry a `Re: ` prefix), not the
        // bare emitted value. Safe because `subjectForEmit`/`stripReplyPrefix`
        // only strip the prefix — a non-empty cosmetic subject always yields a
        // non-empty bare one — so the gate can't disagree with `canProceed`.
        hasSubject: (step.email_subject?.trim().length ?? 0) > 0,
        hasBody: stripHtml(step.email_body || '').length > 0,
      })
    })
    return (
      getSequenceProceedBlockReason({
        hasSteps: validatedSteps.length > 0,
        steps: statuses,
        hasUnknownVariables: unknownVariablesInTemplate.length > 0,
        hasMissingVariables:
          missingCampaignColumnVariablesInTemplate.length > 0,
      }) ?? 'Complete the required fields above to continue.'
    )
  }, [
    readOnly,
    canProceed,
    enableSequenceVariants,
    watchedSteps,
    stepRowMeta,
    validatedSteps.length,
    unknownVariablesInTemplate.length,
    missingCampaignColumnVariablesInTemplate.length,
  ])

  if (isLoadingSequence) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-surface">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Build Your Sequence"
        description="Create personalized emails with a live preview."
        rightSlot={
          readOnly ? (
            <div className="pt-0.5">
              <SequenceSaveStatus status={saveStatus} readOnly />
            </div>
          ) : (
            // Nudge the cluster down ~12px so it sits centered in the top-right
            // pocket instead of hugging the top (the header row is items-start).
            <div className="mt-3 flex items-center gap-2">
              <FbButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onOpenTemplates?.()}
              >
                <span className="flex items-center gap-1.5">
                  <PiStack className="size-4" />
                  Templates
                </span>
              </FbButton>
              <FbButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={(sequence?.steps.length ?? 0) === 0}
                tooltip={
                  (sequence?.steps.length ?? 0) === 0
                    ? 'Add a sequence step first'
                    : undefined
                }
                onClick={() => onOpenSaveTemplate?.()}
              >
                <span className="flex items-center gap-1.5">
                  <PiFloppyDisk className="size-4" />
                  Save as template
                </span>
              </FbButton>
            </div>
          )
        }
      />

      <div className="relative flex flex-1 flex-col overflow-hidden rounded-bl-2xl border-b border-l border-line">
        {/* Card body: editor + live preview (side-by-side on xl). The
            validation banner is pinned below this row, at the card's bottom. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden xl:flex-row">
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <div className="flex items-center gap-2 border-b border-line bg-surface-bright py-3 pl-8 pr-[4.5rem] xl:px-8">
              <PiEnvelopeSimple className="size-4 text-neutral-500" />
              <span className="mt-px text-sm leading-none text-fg-secondary">
                Email Format
              </span>
              <FormatToggle
                plainText={isPlainText}
                onChange={handleFormatChange}
                disabled={readOnly || updateSettingsMutation.isPending}
              />
              {!readOnly && (
                <div className="ml-auto flex items-center gap-1">
                  <FloatingTooltip text={`Undo (${UNDO_SHORTCUT_HINT})`}>
                    <button
                      type="button"
                      aria-label="Undo"
                      disabled={!canUndo(history)}
                      onClick={handleUndo}
                      className={UNDO_REDO_BUTTON_CLASS}
                    >
                      <PiArrowUUpLeft className="size-4" />
                    </button>
                  </FloatingTooltip>
                  <FloatingTooltip text={`Redo (${REDO_SHORTCUT_HINT})`}>
                    <button
                      type="button"
                      aria-label="Redo"
                      disabled={!canRedo(history)}
                      onClick={handleRedo}
                      className={UNDO_REDO_BUTTON_CLASS}
                    >
                      <PiArrowUUpRight className="size-4" />
                    </button>
                  </FloatingTooltip>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-auto bg-surface-bright py-5 pl-6 pr-[4.5rem] xl:px-6">
              <div className="mx-auto max-w-[700px]">
                {isLiveCampaign && !readOnly && (
                  <div className="mb-4">
                    <LiveCampaignBanner />
                  </div>
                )}
                {readOnly && isActiveCampaign && (
                  <Alert
                    color="warning"
                    className="mb-4"
                    description="This campaign is active. Pause the campaign to edit the sequence."
                  />
                )}
                {readOnly && !isActiveCampaign && (
                  <div className="mb-4 rounded-xl border border-line bg-surface-alt px-4 py-3 text-sm text-fg-secondary">
                    This campaign has completed. The sequence is read-only.
                  </div>
                )}
                {watchedSteps.length === 0 ? (
                  <div className="py-16 text-center">
                    <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-brand/10">
                      <PiChatText className="size-7 text-brand-bright" />
                    </div>
                    <h2 className="mb-2 text-lg font-semibold text-fg">
                      Start building your sequence
                    </h2>
                    <p className="mx-auto mb-6 max-w-sm text-sm text-fg-secondary">
                      Add email steps to create your outreach sequence. Each
                      step is a personalized email sent at your chosen interval.
                    </p>
                    <FbButton
                      onClick={addStep}
                      className="gap-2"
                      disabled={readOnly}
                    >
                      <PiPlus className="size-4" />
                      Add First Email Step
                    </FbButton>
                  </div>
                ) : (
                  <div className={cn('space-y-0', readOnly && 'select-text')}>
                    {fields.map((field, index) => {
                      // useFieldArray updates sync; useWatch lags — fall back to field snapshot so a just-added row renders.
                      const step = watchedSteps[index] ?? field
                      if (!step) return null
                      // Variants render nested within their family original
                      // below. With the flag off they are not rendered at all
                      // (collapse to the original), though they remain in the
                      // form to round-trip.
                      if (step.is_variant) return null
                      const meta = stepRowMeta[index]
                      const slotNumber = meta?.slotNumber ?? index + 1
                      const isExpanded = expandedStepIndex === index
                      const isFirstStep = slotNumber === 1
                      // Default-on for steps after the first. An off step starts a
                      // new thread with its own real subject, so it drops the
                      // cosmetic inherited-"Re:" treatment.
                      const threadReplyOn = isThreadReplyOn(step)
                      const isAutoReplyStep = !isFirstStep && threadReplyOn
                      // EmailBison stores wait_in_days as the delay AFTER a step.
                      // The gap shown before this slot is therefore the PREVIOUS
                      // original slot's wait, and edits must target that original
                      // (variants share the slot wait, set from the original on
                      // save). Slot-aware so a preceding family's variants don't
                      // misroute the binding to a variant row.
                      const prevOriginalIndex = originalIndexes[slotNumber - 2]
                      const hasNextOriginal = originalIndexes.some(
                        (i) => i > index
                      )
                      const familyVariantIndexes: number[] = []
                      for (
                        let j = index + 1;
                        j < watchedSteps.length && watchedSteps[j]?.is_variant;
                        j += 1
                      ) {
                        familyVariantIndexes.push(j)
                      }
                      const canRemoveOriginal =
                        originalIndexes.length > 1 &&
                        familyVariantIndexes.length === 0
                      const canAddVariant =
                        familyVariantIndexes.length + 1 < MAX_MESSAGES_PER_STEP
                      const stepSummary = stripHtml(step?.email_body || '')
                      const missingStepSubjectVariables =
                        findCampaignColumnVariablesWithMissingValues(
                          campaignVariables,
                          [step?.email_subject ?? '']
                        )
                      const unknownStepSubjectVariables =
                        findUnknownTemplateVariables(allTemplateVariables, [
                          step?.email_subject ?? '',
                        ])

                      return (
                        <div key={field.fieldKey}>
                          {!isFirstStep && (
                            <div className="flex gap-3">
                              <div className="flex w-7 shrink-0 flex-col items-center">
                                <div className="w-0.5 flex-1 bg-brand/20" />
                              </div>
                              <div className="flex items-center py-2">
                                <label
                                  // FIR-7353: brand-tinted so users notice the
                                  // per-step delay is editable.
                                  className={cn(
                                    'flex items-center gap-2 rounded-full border border-brand/30 bg-brand/5 px-3 py-1.5 text-sm font-medium text-fg transition-colors',
                                    !readOnly &&
                                      'cursor-pointer hover:border-brand/50 hover:bg-brand/10'
                                  )}
                                  title={
                                    readOnly
                                      ? undefined
                                      : 'Click to adjust the delay before this step'
                                  }
                                >
                                  <PiClock className="size-3.5 text-brand-active" />
                                  <span>Wait</span>
                                  <input
                                    type="number"
                                    min={1}
                                    value={
                                      watchedSteps[prevOriginalIndex]
                                        ?.wait_in_days ?? 1
                                    }
                                    disabled={readOnly}
                                    onChange={(event) =>
                                      setValue(
                                        `steps.${prevOriginalIndex}.wait_in_days`,
                                        Math.max(
                                          1,
                                          Number(event.target.value) || 1
                                        ),
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        }
                                      )
                                    }
                                    className="w-10 rounded border border-line bg-surface px-1 py-0.5 text-center text-sm font-medium text-fg outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:cursor-not-allowed"
                                  />
                                  <span>days</span>
                                </label>
                                <span className="ml-1.5 inline-flex">
                                  <HelpTooltip
                                    placement="top"
                                    title="How wait times work"
                                    content="Wait times count calendar days, including weekends. The actual send still follows this campaign's sending schedule — if the wait ends outside your active days or sending window, the email goes out at the next available slot."
                                  />
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="flex gap-3">
                            <div className="flex w-7 shrink-0 flex-col items-center pt-4">
                              <div
                                className={cn(
                                  'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                                  step.email_subject &&
                                    stripHtml(step.email_body || '')
                                    ? 'bg-brand-active text-white'
                                    : 'border-2 border-brand-active/30 bg-brand-active/10 text-brand-active'
                                )}
                              >
                                {slotNumber}
                              </div>
                              {hasNextOriginal && (
                                <div className="mt-2 w-0.5 flex-1 bg-sage-lt" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div
                                className={cn(
                                  'rounded-xl border bg-white transition-all',
                                  isExpanded
                                    ? 'border-brand/30 ring-2 ring-brand/15 shadow-sm'
                                    : 'border-line hover:shadow-sm'
                                )}
                              >
                                <div
                                  className="flex cursor-pointer items-center gap-3 px-4 py-3"
                                  onClick={() =>
                                    setExpandedStepIndex(
                                      isExpanded ? -1 : index
                                    )
                                  }
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      {/* Drag handle hidden until step reordering is wired up. Restore <PiDotsSixVerticalBold className="size-4 shrink-0 text-fg-secondary/60" /> and its import then. */}
                                      {meta?.variantLabel && (
                                        // The original becomes "Variant A" once it
                                        // has a split, mirroring the variant badge.
                                        <span className="inline-flex shrink-0 items-center rounded-full bg-ds2026-badge-primary-bg px-2 py-0.5 text-xs font-medium text-ds2026-badge-primary-text">
                                          {meta.variantLabel}
                                        </span>
                                      )}
                                      {/* my-0: Bootstrap reboot's `p { margin-bottom: 1rem }` otherwise
                                        inflates this flex line and drops the badge below the subject. */}
                                      <p className="my-0 min-w-0 flex-1 truncate text-sm text-fg">
                                        {step.email_subject?.trim() ? (
                                          step.email_subject
                                        ) : (
                                          <span className="italic text-fg-secondary">
                                            No subject line
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                    {!isExpanded && stepSummary && (
                                      <p className="mt-0.5 truncate text-sm text-fg-secondary">
                                        {stepSummary}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      className="flex size-7 items-center justify-center rounded hover:bg-surface-alt text-fg-secondary"
                                      disabled={readOnly}
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        duplicateStep(index)
                                      }}
                                      title="Duplicate step"
                                    >
                                      <PiCopy className="size-3.5" />
                                    </button>
                                    {canRemoveOriginal && (
                                      <button
                                        type="button"
                                        className="flex size-7 items-center justify-center rounded hover:bg-error-bg text-fg-secondary hover:text-error"
                                        disabled={readOnly}
                                        onClick={(event) => {
                                          event.stopPropagation()
                                          removeStepAtIndex(index)
                                        }}
                                        title="Remove step"
                                      >
                                        <PiTrash className="size-3.5" />
                                      </button>
                                    )}
                                    {isExpanded ? (
                                      <PiCaretUp className="size-4 text-fg-secondary" />
                                    ) : (
                                      <PiCaretDown className="size-4 text-fg-secondary" />
                                    )}
                                  </div>
                                </div>

                                {isExpanded && (
                                  <div
                                    className={cn(
                                      'space-y-4 border-t border-line px-4 pb-4',
                                      readOnly && 'pointer-events-none'
                                    )}
                                  >
                                    <div className="pt-4">
                                      <div className="mb-1.5 flex items-center gap-2">
                                        <label className="text-sm text-fg-secondary">
                                          Subject line
                                        </label>
                                        {isAutoReplyStep && (
                                          <span className="inline-flex items-center rounded-full bg-ds2026-badge-gray-bg px-2 py-0.5 text-xs font-medium text-ds2026-badge-gray-text">
                                            Auto Re: from Step 1
                                          </span>
                                        )}
                                      </div>
                                      <BaseTextInput
                                        name={`steps.${index}.email_subject`}
                                        value={step.email_subject ?? ''}
                                        onChange={(value) => {
                                          if (index === 0) {
                                            handlePrimarySubjectChange(
                                              String(value)
                                            )
                                            return
                                          }
                                          setValue(
                                            `steps.${index}.email_subject`,
                                            String(value),
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            }
                                          )
                                        }}
                                        placeholder={
                                          isFirstStep
                                            ? 'Email subject line — use {FIRST_NAME} or any campaign column variable'
                                            : threadReplyOn
                                              ? 'Re: [auto-generated from step 1]'
                                              : 'Subject line for this new thread — use {FIRST_NAME} or any campaign column variable'
                                        }
                                        noMargin
                                      />
                                      <SubjectValidationBanners
                                        unknownVariables={
                                          unknownStepSubjectVariables
                                        }
                                        missingVariables={
                                          missingStepSubjectVariables
                                        }
                                      />
                                      {!isFirstStep && (
                                        <div className="mt-3 flex items-center gap-1.5">
                                          <Switch
                                            labelLeft="Reply in thread"
                                            checked={threadReplyOn}
                                            disabled={readOnly}
                                            onChange={(event) => {
                                              const replyOn =
                                                event.target.checked
                                              setValue(
                                                `steps.${index}.thread_reply`,
                                                replyOn,
                                                { shouldDirty: true }
                                              )
                                              // Reply-in-thread ON inherits the
                                              // cosmetic "Re: {step 1 subject}"
                                              // (same value addStep seeds and the
                                              // cascade propagates); OFF starts a
                                              // new thread, so strip any inherited
                                              // "Re: ...". Either way the now-
                                              // required subject field stays
                                              // consistent with the toggle.
                                              const currentSubject = getValues(
                                                `steps.${index}.email_subject`
                                              )
                                              const nextSubject = replyOn
                                                ? applyInheritedSubjectOnThreadReplyOn(
                                                    currentSubject,
                                                    getValues(
                                                      'steps.0.email_subject'
                                                    )
                                                  )
                                                : clearInheritedSubjectOnThreadReplyOff(
                                                    currentSubject
                                                  )
                                              setValue(
                                                `steps.${index}.email_subject`,
                                                nextSubject,
                                                {
                                                  shouldDirty: true,
                                                  shouldValidate: true,
                                                }
                                              )
                                            }}
                                          />
                                          {threadReplyTip && (
                                            <HelpTooltip
                                              title={threadReplyTip.title}
                                              content={threadReplyTip.content}
                                            />
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    <div>
                                      <div className="mb-1.5 flex items-center justify-between gap-2">
                                        <label className="block text-sm text-fg-secondary">
                                          Email body
                                        </label>
                                        <FormatBadge plainText={isPlainText} />
                                      </div>
                                      <EmailBodyEditor
                                        key={isPlainText ? 'plain' : 'html'}
                                        control={control}
                                        name={`steps.${index}.email_body`}
                                        label=""
                                        placeholder={
                                          'Write your email content here...\n\nUse variables like {FIRST_NAME} to personalize.'
                                        }
                                        hideHelperText
                                        disableToolbar={readOnly}
                                        plainText={isPlainText}
                                        campaignId={campaignId}
                                      />
                                      {index === 0 && (
                                        <p className="mt-1.5 text-sm text-fg-secondary">
                                          Tip: include casual opt-out language
                                          naturally — e.g.{' '}
                                          <span className="italic">
                                            &ldquo;Just reply if you&rsquo;d
                                            rather not hear from me.&rdquo;
                                          </span>{' '}
                                          Vary the wording across campaigns to
                                          avoid pattern matching.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {enableSequenceVariants &&
                            (familyVariantIndexes.length > 0 ||
                              (!readOnly && canAddVariant)) && (
                              <div className="flex gap-3">
                                <div className="w-7 shrink-0" />
                                <div className="min-w-0 flex-1 space-y-2 pb-1 pt-2">
                                  {familyVariantIndexes.map((vIndex) => {
                                    const vStep = watchedSteps[vIndex]
                                    const vMeta = stepRowMeta[vIndex]
                                    const vIsExpanded =
                                      expandedStepIndex === vIndex
                                    const vSummary = stripHtmlForPreview(
                                      vStep?.email_body || ''
                                    )
                                    const vKey =
                                      fields[vIndex]?.fieldKey ??
                                      `variant-${vIndex}`
                                    const vActive = vStep?.active !== false
                                    const vToggling =
                                      togglingStepId != null &&
                                      togglingStepId === vStep?.id
                                    const vLabel =
                                      vMeta?.variantLabel ?? 'Variant'
                                    return (
                                      <div
                                        key={vKey}
                                        className={cn(
                                          'rounded-xl border bg-surface-alt',
                                          vActive
                                            ? 'border-line'
                                            : 'border-dashed border-line'
                                        )}
                                      >
                                        <div
                                          className="flex cursor-pointer items-center gap-2 px-4 py-2.5"
                                          onClick={() =>
                                            setExpandedStepIndex(
                                              vIsExpanded ? -1 : vIndex
                                            )
                                          }
                                        >
                                          <span className="inline-flex shrink-0 items-center rounded-full bg-ds2026-badge-primary-bg px-2 py-0.5 text-xs font-medium text-ds2026-badge-primary-text">
                                            {vLabel}
                                          </span>
                                          {!vActive && (
                                            <span className="inline-flex shrink-0 items-center rounded-full bg-surface-hover px-2 py-0.5 text-xs font-medium text-fg-secondary">
                                              Paused
                                            </span>
                                          )}
                                          {!vIsExpanded && (
                                            // my-0: drop Bootstrap reboot's p margin so the badge centers on the subject.
                                            <p className="my-0 min-w-0 flex-1 truncate text-sm text-fg">
                                              {vStep?.email_subject?.trim() ? (
                                                vStep.email_subject
                                              ) : (
                                                <span className="italic text-fg-secondary">
                                                  No subject line
                                                </span>
                                              )}
                                              {vSummary && (
                                                <span className="text-fg-secondary">
                                                  {' '}
                                                  — {vSummary}
                                                </span>
                                              )}
                                            </p>
                                          )}
                                          <div className="ml-auto flex items-center gap-1.5">
                                            {/* Pause/resume is a live action: EmailBison only
                                              allows it on a launched campaign (it 422s on a
                                              draft), and pausing a losing variant mid-test is
                                              the whole point — so gate on launched, not on
                                              the sequence being editable. */}
                                            {isLiveCampaign &&
                                              vStep?.id != null && (
                                                <button
                                                  type="button"
                                                  disabled={vToggling}
                                                  aria-label={`${vActive ? 'Pause' : 'Resume'} ${vLabel}`}
                                                  title={
                                                    vActive
                                                      ? 'Pause this variant (stops split traffic, keeps its stats)'
                                                      : 'Resume this variant'
                                                  }
                                                  onClick={(event) => {
                                                    event.stopPropagation()
                                                    void handleToggleVariantActive(
                                                      vIndex
                                                    )
                                                  }}
                                                  className={cn(
                                                    'rounded-full border px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50',
                                                    vActive
                                                      ? 'border-brand/40 text-brand-bright hover:bg-brand/5'
                                                      : 'border-line text-fg-secondary hover:bg-surface-hover'
                                                  )}
                                                >
                                                  {vToggling
                                                    ? '…'
                                                    : vActive
                                                      ? 'Pause'
                                                      : 'Resume'}
                                                </button>
                                              )}
                                            {!readOnly && (
                                              <button
                                                type="button"
                                                className="flex size-7 items-center justify-center rounded text-fg-secondary hover:bg-error-bg hover:text-error"
                                                onClick={(event) => {
                                                  event.stopPropagation()
                                                  removeStepAtIndex(vIndex)
                                                }}
                                                title="Remove variant"
                                              >
                                                <PiTrash className="size-3.5" />
                                              </button>
                                            )}
                                            {vIsExpanded ? (
                                              <PiCaretUp className="size-4 text-fg-secondary" />
                                            ) : (
                                              <PiCaretDown className="size-4 text-fg-secondary" />
                                            )}
                                          </div>
                                        </div>
                                        {vIsExpanded && (
                                          <div
                                            className={cn(
                                              'space-y-4 border-t border-line px-4 pb-4',
                                              readOnly && 'pointer-events-none'
                                            )}
                                          >
                                            <div className="pt-4">
                                              <label className="mb-1.5 block text-sm text-fg-secondary">
                                                Subject line
                                              </label>
                                              <BaseTextInput
                                                name={`steps.${vIndex}.email_subject`}
                                                value={
                                                  vStep?.email_subject ?? ''
                                                }
                                                onChange={(value) =>
                                                  setValue(
                                                    `steps.${vIndex}.email_subject`,
                                                    String(value),
                                                    {
                                                      shouldDirty: true,
                                                      shouldValidate: true,
                                                    }
                                                  )
                                                }
                                                placeholder="Variant subject line"
                                                noMargin
                                              />
                                            </div>
                                            <div>
                                              <div className="mb-1.5 flex items-center justify-between gap-2">
                                                <label className="block text-sm text-fg-secondary">
                                                  Email body
                                                </label>
                                                <FormatBadge
                                                  plainText={isPlainText}
                                                />
                                              </div>
                                              <EmailBodyEditor
                                                // Remount on mode toggle so the
                                                // toolbar re-initializes (mirrors
                                                // the original editor); otherwise
                                                // it stays hidden until the row is
                                                // collapsed and re-expanded.
                                                key={
                                                  isPlainText ? 'plain' : 'html'
                                                }
                                                control={control}
                                                name={`steps.${vIndex}.email_body`}
                                                label=""
                                                placeholder="Write this variant's content..."
                                                hideHelperText
                                                disableToolbar={readOnly}
                                                plainText={isPlainText}
                                                campaignId={campaignId}
                                              />
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                  {!readOnly && canAddVariant && (
                                    <button
                                      type="button"
                                      onClick={() => addVariant(index)}
                                      className="flex items-center gap-1.5 text-sm font-medium text-brand-bright transition-colors hover:text-brand-active"
                                    >
                                      <PiPlus className="size-4" />
                                      Add split test variant
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      )
                    })}

                    {!readOnly && (
                      <div className="pt-2">
                        <div className="flex w-7 justify-center">
                          <div className="h-8 w-0.5 bg-sage-lt" />
                        </div>
                        <div className="mt-1 flex items-center gap-3">
                          <div className="flex w-7 shrink-0 justify-center">
                            <div className="flex size-7 items-center justify-center rounded-full border-2 border-dashed border-brand/30">
                              <PiPlus className="size-3.5 text-brand/50" />
                            </div>
                          </div>
                          <FbButton
                            variant="outline"
                            onClick={addStep}
                            className="gap-2"
                          >
                            <PiPlus className="size-4" />
                            Add Email Step
                          </FbButton>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {watchedSteps.length > 0 && (
            <div
              className={cn(
                'absolute bottom-0 right-0 top-0 z-30 flex shrink-0 flex-col rounded-tl-xl border-l border-t border-line bg-surface-alt shadow-xl transition-[width] duration-200',
                previewCollapsed ? 'w-12' : 'w-[min(34rem,92%)]',
                'xl:static xl:z-auto xl:w-[520px] xl:shadow-none 2xl:w-[580px]'
              )}
            >
              {previewCollapsed && (
                <button
                  type="button"
                  onClick={() => setPreviewCollapsed(false)}
                  aria-label="Expand live preview"
                  className="group flex h-full w-full flex-col items-center gap-3 rounded-tl-xl bg-white py-4 text-fg-secondary transition-colors hover:bg-neutral-50 xl:hidden"
                >
                  <PiCaretDoubleLeft className="size-4 text-fg-secondary transition-colors group-hover:text-fg" />
                  <PiEye className="size-4 text-neutral-500" />
                  <span className="text-sm text-fg-secondary [writing-mode:vertical-rl]">
                    Live preview
                  </span>
                </button>
              )}
              {!previewCollapsed && (
                <button
                  type="button"
                  onClick={() => setPreviewCollapsed(true)}
                  aria-label="Collapse live preview"
                  className="absolute left-0 top-4 flex h-14 w-6 -translate-x-full items-center justify-center rounded-l-lg border border-r-0 border-line bg-white text-fg-secondary shadow-md transition-colors hover:bg-neutral-50 hover:text-fg xl:hidden"
                >
                  <PiCaretDoubleRight className="size-3.5" />
                </button>
              )}
              <div
                className={cn(
                  'flex min-h-0 flex-1 flex-col',
                  previewCollapsed && 'hidden xl:flex'
                )}
              >
                <div className="space-y-3 rounded-tl-xl border-b border-line bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PiEye className="size-4 text-neutral-500" />
                      <span className="mt-px text-sm leading-none text-fg-secondary">
                        Live preview
                      </span>
                      <FormatBadge plainText={isPlainText} />
                    </div>
                    <div className="flex items-center gap-2">
                      {previewWarnings.length > 0 && (
                        <StatusBadge color="warning" icon={PiWarning}>
                          Unfilled variables
                        </StatusBadge>
                      )}
                      {previewHasSpintax && (
                        <FloatingTooltip
                          theme="dark"
                          text="Show another spintax variation"
                        >
                          <button
                            type="button"
                            onClick={() => setRandomizeSeed((s) => s + 1)}
                            className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-brand-bright transition-colors hover:text-brand-active"
                          >
                            <PiShuffle className="size-3.5" />
                            Randomize
                          </button>
                        </FloatingTooltip>
                      )}
                      <FloatingTooltip
                        theme="dark"
                        text={
                          !previewStep?.id
                            ? 'Save the sequence first to send a test'
                            : `Send ${previewLabel} as a test`
                        }
                      >
                        <button
                          type="button"
                          onClick={() => setShowTestEmailModal(true)}
                          disabled={readOnly || !previewStep?.id}
                          className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-brand-bright transition-colors hover:text-brand-active disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-brand-bright"
                        >
                          <PiPaperPlaneTilt className="size-3.5" />
                          Send test email
                        </button>
                      </FloatingTooltip>
                    </div>
                  </div>

                  <div className="relative">
                    <button
                      type="button"
                      disabled={
                        showNoIncludedContactsState ||
                        previewContacts.length <= 1
                      }
                      onClick={() => setShowContactDropdown((open) => !open)}
                      className="flex w-full items-center gap-2 rounded-lg border border-line bg-white px-2.5 py-1.5 text-left transition hover:bg-neutral-50 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <div className="flex size-6 items-center justify-center rounded-full bg-brand-sage-100">
                        <PiUser className="size-3 text-brand-sage-700" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-fg-secondary">
                          {activeOverride
                            ? 'Custom email for'
                            : 'Variables filled as'}
                        </p>
                        <p className="truncate text-sm text-fg">
                          {activePreviewContact?.label ??
                            (isUsingSamplePreview
                              ? 'Sample Contact'
                              : 'No included contacts')}
                          {(activePreviewContact?.subtitle ||
                            (isUsingSamplePreview
                              ? 'Example preview until campaign contacts are added'
                              : showNoIncludedContactsState
                                ? 'Go back and include at least one contact'
                                : 'Preview contact')) && (
                            <span className="ml-1 text-sm text-fg-secondary">
                              ·{' '}
                              {activePreviewContact?.subtitle ||
                                (isUsingSamplePreview
                                  ? 'Example preview until campaign contacts are added'
                                  : showNoIncludedContactsState
                                    ? 'Go back and include at least one contact'
                                    : 'Preview contact')}
                            </span>
                          )}
                        </p>
                      </div>
                      <PiCaretDown
                        className={cn(
                          'size-4 shrink-0 text-fg-secondary transition-transform',
                          showContactDropdown && 'rotate-180'
                        )}
                      />
                    </button>
                    {showContactDropdown && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowContactDropdown(false)}
                        />
                        <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-line bg-white py-1 shadow-lg">
                          <p className="px-3 py-1.5 text-sm text-fg-secondary">
                            Preview as contact
                          </p>
                          {previewContacts.map((contact) => (
                            <button
                              key={contact.id}
                              type="button"
                              onClick={() => {
                                setPreviewContactId(contact.id)
                                setShowContactDropdown(false)
                              }}
                              className={cn(
                                'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-surface-alt',
                                previewContactId === contact.id &&
                                  'bg-surface-alt'
                              )}
                            >
                              <div className="flex size-6 items-center justify-center rounded-full bg-brand/10">
                                <PiUser className="size-3 text-brand-bright" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm text-fg">
                                  {contact.label}
                                  {contact.bisonLeadId != null &&
                                    overrideLeadIds.has(
                                      contact.bisonLeadId
                                    ) && (
                                      <StatusBadge
                                        color="info"
                                        className="ml-1.5"
                                      >
                                        Edited
                                      </StatusBadge>
                                    )}
                                </p>
                                <p className="truncate text-sm text-fg-secondary">
                                  {contact.subtitle}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {previewableIndexes.length > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setPreviewStepIndex(
                            previewableIndexes[Math.max(0, previewPosition - 1)]
                          )
                        }
                        disabled={previewPosition === 0}
                        className="flex size-7 items-center justify-center rounded-lg border border-line transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <PiCaretLeft className="size-3.5" />
                      </button>
                      <div className="flex flex-1 flex-wrap items-center justify-center gap-1.5">
                        {previewableIndexes.map((stepIndex) => {
                          const isVariantDot = stepRowMeta[stepIndex]?.isVariant
                          return (
                            <button
                              key={`preview-step-${stepIndex}`}
                              type="button"
                              onClick={() => setPreviewStepIndex(stepIndex)}
                              // Variants render as smaller dots so the nav reads
                              // as messages grouped by slot, not a flat list.
                              className={cn(
                                'rounded-full transition-all',
                                isVariantDot ? 'size-1.5' : 'size-2',
                                stepIndex === safePreviewStepIndex
                                  ? 'scale-125 bg-brand-bright'
                                  : 'bg-fg-secondary/20 hover:bg-fg-secondary/40'
                              )}
                            />
                          )
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setPreviewStepIndex(
                            previewableIndexes[
                              Math.min(
                                previewableIndexes.length - 1,
                                previewPosition + 1
                              )
                            ]
                          )
                        }
                        disabled={
                          previewPosition === previewableIndexes.length - 1
                        }
                        className="flex size-7 items-center justify-center rounded-lg border border-line transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <PiCaretRight className="size-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-auto p-4">
                  {showNoIncludedContactsState ? (
                    <div className="flex h-full items-center justify-center">
                      <div className="max-w-xs rounded-xl border border-dashed border-line bg-white px-6 py-8 text-center">
                        <PiUser className="mx-auto mb-3 size-8 text-fg-secondary/40" />
                        <h3 className="text-sm font-semibold text-fg">
                          No included preview contacts
                        </h3>
                        <p className="mt-2 text-sm text-fg-secondary">
                          Include at least one contact in Review Leads to
                          preview your sequence with real campaign recipients.
                        </p>
                      </div>
                    </div>
                  ) : (
                    previewStep && (
                      <div className="overflow-hidden rounded-xl border border-line bg-white shadow-sm">
                        <div className="flex items-center gap-2 border-b border-line bg-neutral-50 px-4 py-2.5">
                          <div className="flex size-6 items-center justify-center rounded-full bg-brand-active text-xs text-white">
                            {previewSlotNumber}
                          </div>
                          <span className="text-sm text-fg-secondary">
                            {previewMeta?.variantLabel
                              ? `Email Step ${previewSlotNumber} · ${previewMeta.variantLabel}`
                              : `Email Step ${previewSlotNumber} of ${originalIndexes.length}`}
                          </span>
                          {activeOverride && (
                            <StatusBadge color="info">Edited</StatusBadge>
                          )}
                          {previewSlotNumber > 1 && (
                            <span className="ml-auto flex items-center gap-1 text-xs text-fg-secondary">
                              <PiClock className="size-2.5" />
                              {previewIncomingDays} day
                              {previewIncomingDays === 1 ? '' : 's'} after Step{' '}
                              {previewSlotNumber - 1}
                            </span>
                          )}
                        </div>

                        <div className="border-b border-line px-4 py-3">
                          <p className="mb-1 text-sm text-fg-secondary">
                            Subject
                          </p>
                          {filledPreviewSubject ? (
                            <p className="text-sm text-fg">
                              {filledPreviewSubject}
                            </p>
                          ) : (
                            <p className="text-sm italic text-fg-secondary">
                              No subject line
                            </p>
                          )}
                        </div>

                        <div className="px-4 py-4">
                          <p className="mb-2 text-sm text-fg-secondary">Body</p>
                          {hasPreviewBodyContent ? (
                            <EmailPreviewBody
                              renderedBody={filledPreviewBody}
                              isPlainText={isPlainText}
                            />
                          ) : (
                            <div className="py-8 text-center text-sm italic text-fg-secondary">
                              <PiChatText className="mx-auto mb-2 size-6 opacity-30" />
                              No content yet - start typing in the editor
                            </div>
                          )}
                        </div>

                        {previewWarnings.length > 0 && (
                          <div className="border-t border-line px-4 py-3">
                            <Alert
                              color="warning"
                              description={
                                <>
                                  <p className="font-medium">
                                    Some variables could not be filled for this
                                    preview contact:
                                  </p>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {previewWarnings.map((warning) => (
                                      <code
                                        key={warning}
                                        className="rounded bg-surface px-1.5 py-0.5 font-mono text-xxs text-warning-strong"
                                      >
                                        {warning}
                                      </code>
                                    ))}
                                  </div>
                                </>
                              }
                            />
                          </div>
                        )}
                      </div>
                    )
                  )}

                  {previewStep &&
                    activePreviewContact &&
                    !showNoIncludedContactsState && (
                      <div className="mt-3 rounded-lg bg-white/80 px-3 py-2 text-sm text-fg-secondary">
                        <p className="flex items-start gap-2 leading-relaxed">
                          <PiUser className="mt-0.5 size-3.5 shrink-0 text-fg-secondary" />
                          <span>
                            Variables filled as{' '}
                            <span className="font-medium text-fg">
                              {activePreviewContact.label}
                            </span>
                            {activePreviewContact.subtitle
                              ? ` at ${activePreviewContact.subtitle}`
                              : '.'}
                          </span>
                        </p>
                        <SignatureDisclaimer
                          mailboxAddress={mailboxAddress}
                          multipleMailboxesAttached={multipleMailboxesAttached}
                        />
                        {isUsingSamplePreview && (
                          <p className="mt-1.5 flex items-start gap-2 leading-relaxed">
                            <PiWarning className="mt-0.5 size-3.5 shrink-0 text-fg-secondary" />
                            <span>
                              This is a sample preview. Add campaign contacts to
                              see real lead substitutions here.
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Validation banner pinned to the bottom of the card (above the
            footer), like a deal-card stage banner — instead of pushing the
            sequence down from the top. */}
        {!readOnly &&
          showUnknownTemplateBanner &&
          unknownVariablesInTemplate.length > 0 && (
            <div className="flex items-start gap-2 border-t border-alert-danger-border bg-alert-danger-bg px-6 py-3 text-sm text-alert-danger-title">
              <PiWarning className="mt-0.5 size-4 shrink-0" />
              <div>
                <p className="font-medium">
                  This sequence uses variables that are not available for this
                  campaign. Remove them before continuing so emails do not send
                  with unresolved placeholders.
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {unknownVariablesInTemplate.map((variable) => (
                    <code
                      key={variable}
                      className="rounded bg-surface px-1.5 py-0.5 font-mono text-xs text-error-strong"
                    >
                      {variable}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          )}
        {!readOnly &&
          showMissingTemplateBanner &&
          missingCampaignColumnVariablesInTemplate.length > 0 && (
            <div className="flex items-start gap-2 border-t border-alert-warning-border bg-alert-warning-bg px-6 py-3 text-sm text-alert-warning-title">
              <PiWarning className="mt-0.5 size-4 shrink-0" />
              <div>
                <p className="font-medium">
                  This sequence uses campaign column variables with missing
                  values. Fill those cells before continuing so emails do not
                  send with unresolved placeholders.
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {missingCampaignColumnVariablesInTemplate.map((variable) => (
                    <span
                      key={variable.token}
                      className="rounded bg-surface px-1.5 py-0.5 text-xs text-warning-strong"
                    >
                      {variable.label}: {getMissingValuesLabel(variable)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
      </div>

      {bisonCampaignId != null && bisonCampaignId > 0 && (
        <SendStepTestEmailModal
          show={showTestEmailModal}
          onHide={() => setShowTestEmailModal(false)}
          bisonStepId={previewStep?.id ?? null}
          bisonCampaignId={bisonCampaignId}
          stepNumber={previewSlotNumber}
          stepLabel={previewLabel}
          unresolvedVariables={previewWarnings}
          sampleRenderedFor={sampleRenderedFor}
        />
      )}

      <WizardStepFooter>
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <PiCaretLeft className="size-4" />
          {backLabel}
        </FbButton>
        {!readOnly && (
          // Save status + the disabled-Continue reason sit inline, directly left
          // of the button, so neither adds a row to the footer. The reason is
          // closest to the button since it explains why it's disabled.
          <div className="flex min-w-0 items-center justify-end gap-3">
            <SequenceSaveStatus
              // While the step is blocked from continuing, the block reason
              // (rendered to the right) is the actionable message. Autosave is
              // gated off until the criteria are met, so the status sticks at
              // "unsaved" — showing "Unsaved changes…" beside the block reason
              // reads as two competing problems and confuses users. Suppress
              // just that stuck state; saving / saved / error still surface.
              status={
                proceedBlockReason && saveStatus === 'unsaved'
                  ? 'idle'
                  : saveStatus
              }
              lastSavedAt={lastSavedAt}
              subtle
            />
            {proceedBlockReason && (
              <p className="my-0 max-w-xs text-right text-sm text-fg-secondary">
                {proceedBlockReason}
              </p>
            )}
            <FbButton
              type="button"
              onClick={() => void onSubmit()}
              disabled={!canProceed || isSaving}
              className={`${WIZARD_BUTTON_MIN_W} shrink-0 justify-center gap-2`}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="size-4" />
                  Saving...
                </>
              ) : (
                <>
                  Continue to Settings
                  <PiCaretRight className="size-4" />
                </>
              )}
            </FbButton>
          </div>
        )}
      </WizardStepFooter>
    </div>
  )
}
